/**
 * You › Digests (G13): morning/afternoon/evening times, scope, push, and opt-in email.
 * Stored as an encrypted `setting` entity (synced to every trusted device and read by the
 * daemons that compose digests). Email credentials are 1Password `op://` references only.
 */
import { useQueryClient } from '@tanstack/react-query'
import {
  DIGEST_SLOT_LABEL,
  DIGEST_SLOTS,
  type DigestEmailSettings,
  type DigestSettings,
  DigestSettings as DigestSettingsSchema,
  type DigestSlot,
} from '@viko/protocol'
import { Button } from '@viko/transcript-ui'
import { CircleAlert } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import {
  browserZone,
  defaultSettings,
  saveDigestSettings,
  useDigestSettings,
  useDigests,
} from '../lib/digests.js'
import { useProjects } from '../lib/queries.js'
import { DigestView } from './DigestView.js'
import { Ago, errorText, useToast } from './ui.js'

const DEFAULT_EMAIL: DigestEmailSettings = {
  enabled: false,
  from: '',
  to: [],
  slots: ['morning'],
  transport: {
    type: 'smtp',
    host: '',
    port: 465,
    security: 'tls',
    username: '',
    password: 'op://',
  },
}

export const EMAIL_WARNING =
  'Email leaves Viko’s end-to-end encryption. Your own Viko daemon composes the digest and sends it through your mail account; your mail provider and each recipient’s provider can read it. Viko’s servers never see it.'

/** Pure: validation messages for the form (the schema is the source of truth). */
export function digestSettingsErrors(s: DigestSettings): string[] {
  const r = DigestSettingsSchema.safeParse(s)
  if (r.success) return []
  return r.error.issues.map((i) => {
    const path = i.path.join('.')
    if (path.includes('password') || path.includes('apiKey'))
      return 'Credentials must be 1Password references (op://vault/item/field)'
    if (path.includes('email.to') || path.includes('email.from'))
      return 'Enter valid email addresses'
    if (path.includes('time')) return 'Times use HH:MM (24-hour)'
    return `${path || 'settings'}: ${i.message}`
  })
}

export function DigestSettingsPanel() {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const saved = useDigestSettings()
  const projects = useProjects()
  const digests = useDigests()
  const [s, setS] = useState<DigestSettings | undefined>()
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (saved.isSuccess && !s) setS(saved.data ?? defaultSettings())
  }, [saved.isSuccess, saved.data, s])
  if (!b.entities)
    return (
      <p className="m-0 px-4 text-[14px] text-text-3">
        Digests need an unlocked, end-to-end encrypted account.
      </p>
    )
  if (!s) return <p className="m-0 px-4 text-[14px] text-text-3">Loading digest settings…</p>
  const email = s.email ?? DEFAULT_EMAIL
  const setEmail = (e: Partial<DigestEmailSettings>) => setS({ ...s, email: { ...email, ...e } })
  const errors = digestSettingsErrors(s.email && !s.email.enabled ? { ...s, email: undefined } : s)
  const save = async () => {
    if (errors.length) return
    setBusy(true)
    try {
      const entities = b.entities
      if (!entities) throw new Error('Encrypted account locked')
      // A disabled, never-configured email block is not stored.
      const next = s.email && !s.email.enabled && !s.email.from ? { ...s, email: undefined } : s
      const { email: _e, ...rest } = next
      const value = await saveDigestSettings(entities, next.email ? next : (rest as DigestSettings))
      setS(value)
      qc.setQueryData(['digest-settings', b.artifactAccess.accountId], value)
      toast('Digest settings saved')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  const latest = digests.data?.find((d) => d.scope === 'all') ?? digests.data?.[0]
  return (
    <div className="mx-4 grid gap-4 text-[14px]" data-testid="digest-settings">
      <fieldset className="grid gap-2">
        <legend className="font-medium">Times ({s.timezone})</legend>
        {DIGEST_SLOTS.map((slot) => (
          <label key={slot} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={s.slots[slot].enabled}
              onChange={(e) =>
                setS({
                  ...s,
                  slots: { ...s.slots, [slot]: { ...s.slots[slot], enabled: e.target.checked } },
                })
              }
            />
            <span className="w-24">{DIGEST_SLOT_LABEL[slot]}</span>
            <input
              type="time"
              aria-label={`${DIGEST_SLOT_LABEL[slot]} time`}
              className="input !min-h-9 !w-auto !py-1"
              value={s.slots[slot].time}
              onChange={(e) =>
                setS({
                  ...s,
                  slots: { ...s.slots, [slot]: { ...s.slots[slot], time: e.target.value } },
                })
              }
            />
          </label>
        ))}
        {s.timezone !== browserZone() ? (
          <Button size="sm" variant="ghost" onClick={() => setS({ ...s, timezone: browserZone() })}>
            Use this device’s time zone ({browserZone()})
          </Button>
        ) : null}
      </fieldset>
      <fieldset className="grid gap-2">
        <legend className="font-medium">What to send</legend>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={s.crossProject}
            onChange={(e) => setS({ ...s, crossProject: e.target.checked })}
          />
          One digest across all projects
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={s.perProject}
            onChange={(e) => setS({ ...s, perProject: e.target.checked })}
          />
          A digest per project (only when something changed)
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={s.push}
            onChange={(e) => setS({ ...s, push: e.target.checked })}
          />
          Notify me when a digest is ready (the notification carries no content)
        </label>
        {(projects.data ?? []).length ? (
          <details>
            <summary>
              Projects included (
              {(projects.data ?? []).filter((p) => !s.excluded.includes(p.id)).length} of{' '}
              {(projects.data ?? []).length})
            </summary>
            {(projects.data ?? []).map((p) => (
              <label key={p.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!s.excluded.includes(p.id)}
                  onChange={(e) =>
                    setS({
                      ...s,
                      excluded: e.target.checked
                        ? s.excluded.filter((x) => x !== p.id)
                        : [...s.excluded, p.id],
                    })
                  }
                />
                {p.name}
              </label>
            ))}
          </details>
        ) : null}
      </fieldset>
      <fieldset className="grid gap-2">
        <legend className="font-medium">Email (optional)</legend>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={email.enabled}
            onChange={(e) => setEmail({ enabled: e.target.checked })}
          />
          Also email digests from my own mail account
        </label>
        {email.enabled ? (
          <>
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg bg-surface-2 p-3 text-status-wait"
              data-testid="email-warning"
            >
              <CircleAlert size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
              <span className="text-text-1">{EMAIL_WARNING}</span>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!email.acknowledgedAt}
                onChange={(e) => {
                  if (e.target.checked) setEmail({ acknowledgedAt: Date.now() })
                  else {
                    const { acknowledgedAt: _a, ...rest } = email
                    setS({ ...s, email: rest })
                  }
                }}
              />
              I understand; send digests by email
            </label>
            <label className="grid gap-1">
              From
              <input
                className="input"
                type="email"
                value={email.from}
                onChange={(e) => setEmail({ from: e.target.value.trim() })}
              />
            </label>
            <label className="grid gap-1">
              To (comma-separated)
              <input
                className="input"
                value={email.to.join(', ')}
                onChange={(e) =>
                  setEmail({
                    to: e.target.value
                      .split(',')
                      .map((x) => x.trim())
                      .filter(Boolean),
                  })
                }
              />
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <span>Email these:</span>
              {DIGEST_SLOTS.map((slot: DigestSlot) => (
                <label key={slot} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={email.slots.includes(slot)}
                    onChange={(e) =>
                      setEmail({
                        slots: e.target.checked
                          ? [...email.slots, slot]
                          : email.slots.filter((x) => x !== slot),
                      })
                    }
                  />
                  {DIGEST_SLOT_LABEL[slot]}
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!email.perProject}
                onChange={(e) => setEmail({ perProject: e.target.checked })}
              />
              Also email per-project digests
            </label>
            <label className="grid gap-1">
              Send with
              <select
                className="input"
                value={email.transport.type === 'smtp' ? 'smtp' : email.transport.provider}
                onChange={(e) =>
                  setEmail({
                    transport:
                      e.target.value === 'smtp'
                        ? DEFAULT_EMAIL.transport
                        : {
                            type: 'api',
                            provider: e.target.value as 'resend' | 'postmark',
                            apiKey: 'op://',
                          },
                  })
                }
              >
                <option value="smtp">SMTP (your mail account)</option>
                <option value="resend">Resend API</option>
                <option value="postmark">Postmark API</option>
              </select>
            </label>
            {email.transport.type === 'smtp' ? (
              <SmtpFields
                t={email.transport}
                onChange={(t) => setEmail({ transport: { ...email.transport, ...t } as never })}
              />
            ) : (
              <label className="grid gap-1">
                API key (1Password reference)
                <input
                  className="input font-mono"
                  value={email.transport.apiKey}
                  placeholder="op://Private/Resend/credential"
                  onChange={(e) =>
                    setEmail({
                      transport: { ...email.transport, apiKey: e.target.value.trim() } as never,
                    })
                  }
                />
              </label>
            )}
          </>
        ) : null}
      </fieldset>
      {errors.length ? (
        <ul className="m-0 pl-5 text-status-fail" aria-label="Problems">
          {[...new Set(errors)].map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={busy || !!errors.length}
          onClick={() => void save()}
        >
          Save
        </Button>
        {s.updatedAt ? (
          <span className="self-center text-text-3">
            Saved <Ago ts={s.updatedAt} />
          </span>
        ) : null}
      </div>
      {latest ? (
        <details>
          <summary>
            Latest digest · {latest.headline} · <Ago ts={latest.generatedAt} />
          </summary>
          <DigestView digest={latest} />
        </details>
      ) : null}
    </div>
  )
}

function SmtpFields({
  t,
  onChange,
}: {
  t: Extract<DigestEmailSettings['transport'], { type: 'smtp' }>
  onChange: (t: Partial<Extract<DigestEmailSettings['transport'], { type: 'smtp' }>>) => void
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <label className="grid gap-1">
        SMTP host
        <input
          className="input"
          value={t.host}
          onChange={(e) => onChange({ host: e.target.value.trim() })}
        />
      </label>
      <label className="grid gap-1">
        Port
        <input
          className="input"
          type="number"
          value={t.port}
          onChange={(e) => onChange({ port: Number(e.target.value) })}
        />
      </label>
      <label className="grid gap-1">
        Security
        <select
          className="input"
          value={t.security}
          onChange={(e) => onChange({ security: e.target.value as 'tls' | 'starttls' })}
        >
          <option value="tls">TLS (port 465)</option>
          <option value="starttls">STARTTLS (port 587)</option>
        </select>
      </label>
      <label className="grid gap-1">
        Username (or op:// reference)
        <input
          className="input"
          value={t.username}
          onChange={(e) => onChange({ username: e.target.value.trim() })}
        />
      </label>
      <label className="grid gap-1 sm:col-span-2">
        Password (1Password reference)
        <input
          className="input font-mono"
          value={t.password}
          placeholder="op://Private/Mail/app-password"
          onChange={(e) => onChange({ password: e.target.value.trim() })}
        />
      </label>
    </div>
  )
}
